/**
 * Exit Intent VIDEO Popup — Empty-cart variant
 * --------------------------------------------
 * Renders on every page (via layout/theme.liquid). Fires when the visitor
 * shows intent to leave AND the cart is currently EMPTY (item_count === 0).
 *
 * Desktop: mouseleave at top of viewport + tab hidden
 * Mobile:  back button + app switch + scroll-up burst + idle timeout
 *
 * Differences from the regular exit-intent popup:
 *   - Fires only when cart is EMPTY (regular one fires when cart has items)
 *   - NO cooldown, NO session limit — re-shows on every exit attempt while
 *     the cart stays empty
 *   - Content is just a video (autoplay, muted, with an unmute toggle)
 *
 * Lifecycle:
 *   1. Wait for delay_seconds, then verify the cart is empty (live /cart.js)
 *   2. If empty → arm exit-intent triggers
 *   3. On trigger → re-check cart is empty → show modal, autoplay muted
 *   4. On close → reset video, keep triggers armed so it can fire again
 */

(function () {
  'use strict';

  const popup = document.getElementById('ExitIntentVideoPopup');
  if (!popup) return;

  const config = {
    delaySeconds: parseInt(popup.dataset.delaySeconds, 10) || 3,
    mobileIdleSeconds: parseInt(popup.dataset.mobileIdleSeconds, 10) || 30,
  };

  const video = popup.querySelector('#ExitIntentVideoPlayer');
  const soundBtn = popup.querySelector('[data-exit-intent-video-sound]');

  // =========================================
  // SESSION FIRE-ONCE
  // Show the video at most once per browser session. After it has fired,
  // triggers are detached and never re-armed until a new session (tab reopen).
  // =========================================
  const SESSION_KEY = 'exitIntentVideoFiredThisSession';

  function firedThisSession() {
    try {
      return sessionStorage.getItem(SESSION_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  function markFired() {
    try {
      sessionStorage.setItem(SESSION_KEY, '1');
    } catch (e) { /* storage blocked, no-op */ }
  }

  // =========================================
  // LIVE CART CHECK — popup fires only when cart is EMPTY
  // =========================================
  function fetchCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => (data && typeof data.item_count === 'number' ? data.item_count : null))
      .catch(() => null);
  }

  // =========================================
  // MODAL SHOW / HIDE
  // =========================================
  let lastFocusedElement = null;
  let showInProgress = false;
  // Last known cart item_count. Kept fresh at arm time and whenever the tab
  // becomes visible again, so showPopup() can open INSTANTLY off the cache
  // instead of blocking on a /cart.js round-trip. The live re-check still
  // runs in the background and retracts the popup if the cart changed.
  let cachedCount = null;

  function isOpen() {
    return !popup.hidden;
  }

  // Actually reveal the modal + start playback. No cart gate here — callers
  // are responsible for the empty-cart check.
  function openModal() {
    lastFocusedElement = document.activeElement;
    popup.hidden = false;
    popup.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('exit-intent-video-active'); // blurs page behind

    // Autoplay muted (always allowed by browsers). User can unmute.
    if (video) {
      video.muted = true;
      popup.dataset.muted = 'true';
      video.currentTime = 0;
      const p = video.play();
      if (p && typeof p.catch === 'function') p.catch(function () { /* ignore */ });
    }

    const closeBtn = popup.querySelector('[data-exit-intent-video-close]');
    if (closeBtn) closeBtn.focus();

    // Re-arm mobile back-button trap so a second back press can re-fire.
    if (isTouchDevice) {
      history.pushState({ exitIntentVideoArmed: true }, '');
    }

    document.dispatchEvent(new CustomEvent('exit-intent-video:shown'));
  }

  // Lock the popup in as "fired" once the empty cart is confirmed, so it
  // never shows again this session.
  function finalize() {
    markFired();
    detachTriggers();
  }

  function showPopup() {
    if (isOpen() || showInProgress || firedThisSession()) return;

    // Fast path: cache says the cart is empty → open immediately, then
    // verify live in the background. Only lock in once confirmed empty; if
    // the cart is no longer empty, retract and stay armed.
    if (cachedCount === 0) {
      openModal();
      fetchCartCount().then((count) => {
        if (count !== null) cachedCount = count;
        if (count !== null && count > 0) {
          hidePopup(); // cart changed → retract, keep armed
        } else {
          finalize();  // confirmed empty → show once, then stop
        }
      });
      return;
    }

    // Cache unknown or non-empty → verify first so we never flash the popup
    // over a cart that actually has items.
    showInProgress = true;
    fetchCartCount().then((count) => {
      if (count !== null) cachedCount = count;
      showInProgress = false;
      if (count === 0) {
        openModal();
        finalize();
      }
    });
  }

  function hidePopup() {
    popup.hidden = true;
    popup.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.classList.remove('exit-intent-video-active');

    if (video) {
      video.pause();
      video.currentTime = 0;
      video.muted = true;
      popup.dataset.muted = 'true';
    }

    if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
      lastFocusedElement.focus();
    }
  }

  // =========================================
  // DEVICE DETECTION
  // =========================================
  const isTouchDevice = ('ontouchstart' in window) || navigator.maxTouchPoints > 0;

  // =========================================
  // DESKTOP TRIGGER: mouse exits top of viewport
  // =========================================
  function onMouseLeave(e) {
    if (e.clientY <= 0) showPopup();
  }

  // =========================================
  // MOBILE TRIGGER 1: Back button
  // =========================================
  function onPopState() {
    showPopup();
    history.pushState({ exitIntentVideoArmed: true }, '');
  }

  // =========================================
  // TRIGGER: App switch / tab hidden (desktop + mobile)
  // =========================================
  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      showPopup();
    } else {
      // Returned to the tab → refresh the cached cart count so the next
      // exit attempt opens instantly with an accurate gate.
      fetchCartCount().then((count) => {
        if (count !== null) cachedCount = count;
      });
    }
  }

  // =========================================
  // MOBILE TRIGGER 3: Rapid scroll-up burst
  // =========================================
  let lastScrollY = window.scrollY;
  let lastScrollTime = Date.now();

  function onScroll() {
    const now = Date.now();
    const scrollY = window.scrollY;
    const deltaY = lastScrollY - scrollY;
    const deltaTime = now - lastScrollTime;

    if (deltaY > 120 && deltaTime < 250 && scrollY < 300) {
      showPopup();
    }

    lastScrollY = scrollY;
    lastScrollTime = now;
  }

  // =========================================
  // MOBILE TRIGGER 4: Idle timeout
  // =========================================
  let idleTimer = null;
  const IDLE_TIMEOUT_MS = config.mobileIdleSeconds * 1000;

  function resetIdleTimer() {
    clearTimeout(idleTimer);
    idleTimer = setTimeout(showPopup, IDLE_TIMEOUT_MS);
  }

  function attachIdleTracker() {
    ['touchstart', 'touchmove', 'click', 'scroll'].forEach((evt) => {
      document.addEventListener(evt, resetIdleTimer, { passive: true });
    });
    resetIdleTimer();
  }

  // =========================================
  // ATTACH / DETACH TRIGGERS (device-branched)
  // Triggers stay armed until the popup fires once. After it fires (empty
  // cart confirmed) they are detached and not re-armed until a new session.
  // showPopup() also re-checks the cart each time and no-ops if not empty.
  // =========================================
  function attachTriggers() {
    if (isTouchDevice) {
      history.pushState({ exitIntentVideoArmed: true }, '');
      window.addEventListener('popstate', onPopState);
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('scroll', onScroll, { passive: true });
      attachIdleTracker();
    } else {
      document.addEventListener('mouseleave', onMouseLeave);
      document.addEventListener('visibilitychange', onVisibilityChange);
    }
  }

  function detachTriggers() {
    document.removeEventListener('mouseleave', onMouseLeave);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('popstate', onPopState);
    clearTimeout(idleTimer);
    ['touchstart', 'touchmove', 'click', 'scroll'].forEach((evt) => {
      document.removeEventListener(evt, resetIdleTimer);
    });
  }

  // Arm triggers after delay — but only if the cart is currently empty AND
  // the popup hasn't already fired this session.
  if (firedThisSession()) return;
  fetchCartCount().then((count) => {
    if (count !== null) cachedCount = count;
    if (count === null || count > 0) return; // cart has items → never arm
    // Warm the video now (cart is empty) so it plays instantly on exit
    // instead of loading only after the trigger fires.
    if (video) {
      try {
        video.preload = 'auto';
        video.load();
      } catch (e) { /* ignore */ }
    }
    setTimeout(attachTriggers, config.delaySeconds * 1000);
  });

  // =========================================
  // CLOSE HANDLERS
  // =========================================
  popup.querySelectorAll('[data-exit-intent-video-close]').forEach((el) => {
    el.addEventListener('click', hidePopup);
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !popup.hidden) hidePopup();
  });

  // =========================================
  // SOUND TOGGLE (unmute / mute)
  // =========================================
  if (soundBtn && video) {
    soundBtn.addEventListener('click', function () {
      video.muted = !video.muted;
      popup.dataset.muted = video.muted ? 'true' : 'false';
      // If unmuting and the video was somehow paused, resume.
      if (!video.muted && video.paused) {
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(function () { /* ignore */ });
      }
    });
  }
})();
